<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'dashboard/composables/store';

const { t, locale } = useI18n();
const store = useStore();

const filters = ref({
  inboxId: null,
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLoading = ref(false);
const isExporting = ref(false);
const report = ref({
  totalCount: 0,
  pendingCount: 0,
  sentCount: 0,
  templates: [],
  rows: [],
});

const localeCode = computed(() => locale.value?.replace('_', '-') || 'es');

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
  inboxId: filters.value.inboxId,
}));

const formatDateTime = value => {
  if (!value) return '--';

  return new Intl.DateTimeFormat(localeCode.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusLabel = status =>
  t(`RESCHEDULE_REPORTS.STATUSES.${status.toUpperCase()}`, status);

const fetchReport = async () => {
  try {
    isLoading.value = true;
    const { data } = await ReportsAPI.getRescheduleSummary(payload.value);
    report.value = {
      totalCount: data.total_count || 0,
      pendingCount: data.pending_count || 0,
      sentCount: data.sent_count || 0,
      templates: data.templates || [],
      rows: data.rows || [],
    };
  } finally {
    isLoading.value = false;
  }
};

const exportReport = async () => {
  try {
    isExporting.value = true;
    const { data } = await ReportsAPI.downloadRescheduleSummary(payload.value);
    const timestamp = filters.value.dateRange?.to || getUnixTime(new Date());
    downloadCsvFile(`reschedule-summary-${timestamp}.csv`, data);
  } finally {
    isExporting.value = false;
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  await fetchReport();
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('RESCHEDULE_REPORTS.HEADER')"
      :header-description="$t('RESCHEDULE_REPORTS.DESCRIPTION')"
    >
      <NextButton
        slate
        :label="$t('RESCHEDULE_REPORTS.DOWNLOAD')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        @click="exportReport"
      />
    </ReportHeader>

    <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
      <div class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <SearchDateRangeSelector
          v-model="filters.dateRange"
          class="min-w-0 max-w-full"
          @change="fetchReport"
        />
        <div class="w-px h-4 bg-n-weak flex-shrink-0 hidden lg:block" />
        <SearchInboxSelector
          v-model="filters.inboxId"
          :label="$t('RESCHEDULE_REPORTS.INBOX_FILTER')"
          @change="fetchReport"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">{{ $t('RESCHEDULE_REPORTS.TOTAL') }}</p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.totalCount }}</p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">{{ $t('RESCHEDULE_REPORTS.PENDING') }}</p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.pendingCount }}</p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="mb-1 text-sm text-n-slate-11">{{ $t('RESCHEDULE_REPORTS.SENT') }}</p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">{{ report.sentCount }}</p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">{{ $t('RESCHEDULE_REPORTS.LOADING') }}</p>
    </div>

    <div
      v-else-if="report.rows.length === 0"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">{{ $t('RESCHEDULE_REPORTS.EMPTY_STATE') }}</p>
    </div>

    <section v-else class="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ $t('RESCHEDULE_REPORTS.BY_TEMPLATE') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.TEMPLATE') }}</th>
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.TOTAL') }}</th>
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.PENDING') }}</th>
                <th class="py-2">{{ $t('RESCHEDULE_REPORTS.COLUMNS.SENT') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.templates"
                :key="row.template_name"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">{{ row.template_name }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.total_count }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.pending_count }}</td>
                <td class="py-2 text-n-slate-12">{{ row.sent_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4 xl:col-span-1">
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ $t('RESCHEDULE_REPORTS.RECENT_ACTIVITY') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.STATUS') }}</th>
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.CONVERSATION') }}</th>
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.TEMPLATE') }}</th>
                <th class="py-2 pr-3">{{ $t('RESCHEDULE_REPORTS.COLUMNS.SCHEDULED_AT') }}</th>
                <th class="py-2">{{ $t('RESCHEDULE_REPORTS.COLUMNS.SENT_AT') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.rows"
                :key="row.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">{{ statusLabel(row.status) }}</td>
                <td class="py-2 pr-3 text-n-slate-12">#{{ row.conversation_display_id }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.template_name }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ formatDateTime(row.scheduled_at) }}</td>
                <td class="py-2 text-n-slate-12">{{ formatDateTime(row.sent_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>
